/**
 * Before/After Slider — <fit-before-after> custom element.
 *
 * Drives an interactive image comparison via a native <input type="range">
 * for built-in keyboard and screen-reader accessibility.  The range value
 * (0-100) maps to a --fit-bas-position CSS custom property that controls
 * clip-path, divider, and handle positions.
 *
 * Features:
 *  - Horizontal + vertical orientation (data-orientation)
 *  - Click-to-jump and drag via pointer events on the range overlay
 */
(function () {
  'use strict';

  if (customElements.get('fit-before-after')) return;

  /* ── Custom element ── */

  class FitBeforeAfter extends HTMLElement {
    connectedCallback() {
      this._viewport = this.querySelector('.fit-bas__viewport');
      this._range    = this.querySelector('.fit-bas__range');
      if (!this._viewport || !this._range) return;

      this._orientation   = this.dataset.orientation || 'horizontal';
      this._initial       = parseFloat(this.dataset.initial) || 50;
      this._dragging      = false;

      /* Bind handlers once so they can be removed cleanly */
      this._onInput       = this._onInput.bind(this);
      this._onKeyDown     = this._onKeyDown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp   = this._onPointerUp.bind(this);

      this._range.addEventListener('input',       this._onInput);
      this._range.addEventListener('keydown',     this._onKeyDown);
      this._viewport.addEventListener('pointerdown', this._onPointerDown);

      this._setPosition(this._initial);
    }

    disconnectedCallback() {
      if (this._range) {
        this._range.removeEventListener('input',       this._onInput);
        this._range.removeEventListener('keydown',     this._onKeyDown);
      }
      if (this._viewport) {
        this._viewport.removeEventListener('pointerdown', this._onPointerDown);
      }
      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup',   this._onPointerUp);
    }

    /* ── Position ── */

    _setPosition(value) {
      var clamped = Math.max(0, Math.min(100, value));
      this._viewport.style.setProperty('--fit-bas-position', clamped + '%');
      if (this._range) {
        this._range.value = Math.round(clamped);
        this._range.setAttribute('aria-valuetext', Math.round(clamped) + '%');
      }
    }

    /* ── Range input events ── */

    _onInput(e) {
      this._setPosition(parseFloat(e.target.value));
    }

    /* ── Pointer events (click-to-jump + drag) ── */

    _onPointerDown(e) {
      if (e.button !== 0) return;
      e.preventDefault();
      this._dragging = true;
      if (this._viewport.setPointerCapture) {
        this._viewport.setPointerCapture(e.pointerId);
      }
      this._range.focus({ preventScroll: true });
      this._setPosition(this._positionFromPointer(e));
      document.addEventListener('pointermove', this._onPointerMove);
      document.addEventListener('pointerup',   this._onPointerUp);
    }

    _onPointerMove(e) {
      if (!this._dragging) return;
      e.preventDefault();
      this._setPosition(this._positionFromPointer(e));
    }

    _onPointerUp(e) {
      this._dragging = false;
      if (e && this._viewport.releasePointerCapture) {
        this._viewport.releasePointerCapture(e.pointerId);
      }
      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup',   this._onPointerUp);
    }

    _positionFromPointer(e) {
      var rect = this._viewport.getBoundingClientRect();
      if (this._orientation === 'vertical') {
        return ((e.clientY - rect.top) / rect.height) * 100;
      }
      return ((e.clientX - rect.left) / rect.width) * 100;
    }

    /* ── Keyboard: remap arrows for vertical orientation ── */

    _onKeyDown(e) {
      if (this._orientation !== 'vertical') return;
      var val  = parseFloat(this._range.value);
      var step = e.shiftKey ? 10 : 1;

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this._range.value = Math.max(0, val - step);
        this._setPosition(parseFloat(this._range.value));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this._range.value = Math.min(100, val + step);
        this._setPosition(parseFloat(this._range.value));
      }
    }

  }

  customElements.define('fit-before-after', FitBeforeAfter);
})();
